import { Api } from "../myApi";
import { getAuthClient } from "@trumpf/ux.utils";
import { API_PATHS, getApiBaseUrl } from "../config/apiConfig";
import { authClientName } from "../config/authConfig";
import { debug } from "../utils/utils";

export interface IChatData {
    sessionId: string;
    author: string;
    group: string;
    timestamp: string;
    messages: IMessage[];
}

export interface IMessage {
    source: "bot" | "user" | "system";
    name?: string;
    message: string;
    timestamp?: string;
}

export interface ICase {
    caseId: string;
    chats: IChatData[];
    status: {
        status: string;
        hasUserChat: boolean;
        hasTechnicanChat: boolean;
    };
}

export interface IError {
    errorCode: number;
    errorMessage: string; // Can be a translation key
    buttonText?: string;
    showIcon?: boolean;
    params?: Record<string, string | number>; // More specific type for interpolation params
}

// Custom error types for better error handling
export class ChatServiceError extends Error {
    constructor(
        public readonly code: number,
        message: string,
        public readonly originalError?: unknown
    ) {
        super(message);
        this.name = "ChatServiceError";
    }
}

export class NetworkError extends ChatServiceError {
    constructor(message: string, originalError?: unknown) {
        super(503, message, originalError);
        this.name = "NetworkError";
    }
}

export class AuthenticationError extends ChatServiceError {
    constructor(message: string, originalError?: unknown) {
        super(401, message, originalError);
        this.name = "AuthenticationError";
    }
}

export class NotFoundError extends ChatServiceError {
    constructor(message: string, originalError?: unknown) {
        super(404, message, originalError);
        this.name = "NotFoundError";
    }
}

async function withRetry<T>(operation: () => Promise<T>, { maxRetries = 1, baseDelay = 1_000, maxDelay = 5_000 } = {}): Promise<T> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (err) {
            lastError = err;

            // abort-reasons you do **not** want to retry
            if (err instanceof AuthenticationError || err instanceof NotFoundError) {
                throw err;
            }

            // if this was the last allowed attempt – break immediately
            if (attempt === maxRetries) {
                break;
            }

            const delayMs = Math.min(baseDelay * 2 ** (attempt - 1), maxDelay);
            console.warn(`attempt ${attempt} failed, waiting ${delayMs} ms`, err);
            await new Promise((r) => setTimeout(r, delayMs));
        }
    }
    throw lastError;
}

// Enhanced Error Dispatcher with more features
export const ErrorDispatcher = {
    listeners: new Set<(error: IError) => void>(),

    dispatchError(error: IError) {
        // Dispatch custom event
        const event = new CustomEvent("globalError", { detail: error });
        window.dispatchEvent(event);

        // Notify all listeners
        this.listeners.forEach((listener) => listener(error));

        // Log to console in non-production environments
        if (process.env.NODE_ENV !== "production") {
            console.error("[ErrorDispatcher]", error);
        }
    },

    addEventListener(listener: (error: IError) => void) {
        this.listeners.add(listener);
    },

    removeEventListener(listener: (error: IError) => void) {
        this.listeners.delete(listener);
    }
};

// Initialize the API client
const api = new Api({
    baseUrl: getApiBaseUrl(),
    baseApiParams: {
        secure: true,
        headers: {
            "Content-Type": "application/json"
        }
    }
});

/**
 * Interface for middleware parameters that can be passed to backend requests
 */
export interface IMiddlewareParams {
    machineName?: string;
    machineNumber?: string;
    softwareType?: string;
    softwareModule?: string;
    errorDescription?: string;
    errorNumbers?: string;
}

/**
 * Fetches chats for a specific case ID.
 * @param {string} caseId The ID of the case.
 * @param {IMiddlewareParams} middlewareParams Optional parameters to pass to middleware.
 * @returns {Promise<IChatData[]>} An array of chats for the case.
 */
export async function fetchChats(caseId: string, middlewareParams?: IMiddlewareParams): Promise<IChatData[]> {
    try {
        // Get the JWT token from the authClient using methods from the documentation
        const authClient = getAuthClient(authClientName);
        // Try ID token first, then fall back to access token
        const jwtToken = authClient.getIdToken() || authClient.getAccessToken();

        if (!jwtToken) {
            throw new AuthenticationError("No authentication token available");
        }

        let url = `${getApiBaseUrl()}${API_PATHS.CHATS(caseId)}`;
        debug.log(this, "fetchChats", { url });

        // Add middleware params to URL if present
        if (middlewareParams && Object.keys(middlewareParams).length > 0) {
            const queryParams = new URLSearchParams();

            Object.entries(middlewareParams).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, value);
                }
            });

            const queryString = queryParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
        }

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwtToken}`
            }
        });

        if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
                throw new AuthenticationError("Authentication failed");
            } else if (response.status === 404) {
                throw new NotFoundError(`Case with ID ${caseId} not found`);
            } else {
                throw new ChatServiceError(response.status, `Failed to fetch chats: ${response.statusText}`);
            }
        }

        const data = await response.json();

        return data;
    } catch (error) {
        if (error instanceof AuthenticationError || error instanceof NotFoundError || error instanceof ChatServiceError) {
            throw error;
        } else if (error instanceof TypeError && error.message.includes("fetch")) {
            throw new NetworkError("Network error occurred while fetching chats");
        } else {
            throw new ChatServiceError(500, `Unexpected error: ${error.message}`);
        }
    }
}

/**
 * Fetches the status of a specific case.
 * @param {string} caseId The ID of the case.
 * @param {IMiddlewareParams} middlewareParams Optional parameters to pass to middleware.
 * @returns {Promise<{ status: string; hasUserChat: boolean; hasTechnicanChat: boolean }>} The status of the case.
 */
export async function getCaseStatus(
    caseId: string,
    middlewareParams?: IMiddlewareParams
): Promise<{ status: string; hasUserChat: boolean; hasTechnicanChat: boolean }> {
    return withRetry(async () => {
        try {
            // Get the JWT token from the authClient using methods from the documentation
            const authClient = getAuthClient(authClientName);
            // Try ID token first, then fall back to access token
            const jwtToken = authClient.getIdToken() || authClient.getAccessToken();

            if (!jwtToken) {
                throw new AuthenticationError("No authentication token available");
            }

            let url = `${getApiBaseUrl()}${API_PATHS.STATUS(caseId)}`;

            // Add middleware params to URL if present
            if (middlewareParams && Object.keys(middlewareParams).length > 0) {
                const queryParams = new URLSearchParams();

                Object.entries(middlewareParams).forEach(([key, value]) => {
                    if (value !== undefined) {
                        queryParams.append(key, value);
                    }
                });

                const queryString = queryParams.toString();
                if (queryString) {
                    url += `?${queryString}`;
                }
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwtToken}`
                }
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    throw new AuthenticationError("Authentication failed");
                } else if (response.status === 404) {
                    throw new NotFoundError(`Case with ID ${caseId} not found`);
                } else {
                    throw new ChatServiceError(response.status, `Failed to fetch case status: ${response.statusText}`);
                }
            }

            const data = await response.json();
            return {
                status: data.status || "unknown",
                hasUserChat: data.hasUserChat || false,
                hasTechnicanChat: data.hasTechnicanChat || false
            };
        } catch (error) {
            if (error instanceof ChatServiceError) {
                throw error;
            }

            const serviceError = new ChatServiceError(500, `Error fetching status for case ${caseId}`, error);
            ErrorDispatcher.dispatchError({
                errorCode: serviceError.code,
                errorMessage: "error.fetchCaseStatus"
            });
            throw serviceError;
        }
    });
}

/**
 * Fetches the status of a specific case and returns only the status as a string.
 * @param {string} caseId - The ID of the case.
 * @param {IMiddlewareParams} middlewareParams - Optional parameters to pass to middleware.
 * @returns {Promise<string>} A promise that resolves to the status of the case.
 * If an error occurs, it returns 'unknown'.
 */
export async function getCaseStatusString(caseId: string, middlewareParams?: IMiddlewareParams): Promise<string> {
    try {
        const { status } = await getCaseStatus(caseId, middlewareParams);
        return status;
    } catch (error) {
        ErrorDispatcher.dispatchError({
            errorCode: error.code || 500,
            errorMessage: "error.fetchCaseStatus"
        });
        return "error"; // Changed from 'unknown' to be more explicit about the error state
    }
}

/**
 * Adds a new chat to a specific case.
 * @param {string} caseId The ID of the case.
 * @param {string} author The author of the chat (e.g., 'Technican' or 'User').
 * @param {string} group The group of the chat (e.g., 'Technican' or 'User').
 * @returns {Promise<IChatData>} The newly added chat.
 */
export async function addChat(caseId: string, author: string, group: string): Promise<IChatData> {
    try {
        const newChat = {
            sessionId: `s-${Date.now()}`,
            author: author as "Technican" | "User",
            group: group as "Technican" | "User",
            timestamp: new Date().toISOString()
        };

        // Get the JWT token from the authClient using methods from the documentation
        const authClient = getAuthClient(authClientName);
        // Try ID token first, then fall back to access token
        const jwtToken = authClient.getIdToken() || authClient.getAccessToken();

        if (!jwtToken) {
            throw new AuthenticationError("No authentication token available");
        }

        // Pass the token in the Authorization header
        const response = await api.cases.chatsCreate(caseId, newChat, {
            headers: {
                Authorization: `Bearer ${jwtToken}`
            }
        });

        // Ensure the response data conforms to IChatData, potentially mapping fields if needed
        // This assumes the response structure directly matches IChatData
        if (!response.data || typeof response.data.sessionId !== "string") {
            // Handle cases where the response might not be as expected
            // For now, we'll cast, but more robust handling might be needed
            console.warn("API response for addChat might not match IChatData structure", response.data);
            // Depending on API contract, might need default values or further checks
        }

        // Cast response.data to IChatData after validation or ensure API guarantees the structure
        return response.data as IChatData; // Adjust casting based on actual API response structure
    } catch (error) {
        console.error(`Error adding chat to case ${caseId}:`, error);

        // Handle specific error types if necessary
        if (error instanceof AuthenticationError) {
            ErrorDispatcher.dispatchError({ errorCode: error.code, errorMessage: error.message });
        } else {
            // Generic error handling
            ErrorDispatcher.dispatchError({
                errorCode: error.response?.status || 500, // Attempt to get status from error if available
                errorMessage: "error.addChat" // Use a translation key
            });
        }
        // Rethrow or handle the error appropriately
        throw error; // Rethrowing allows the caller to handle it
    }
}

/**
 * Deletes all chats for a specific case.
 * @param {string} caseId The ID of the case.
 * @returns {Promise<void>} A promise that resolves when all chats are deleted.
 */
export async function deleteAllChats(caseId: string): Promise<void> {
    try {
        await api.cases.chatsDelete(caseId);
    } catch (error) {
        console.error(`Error deleting all chats for case ${caseId}:`, error);
        ErrorDispatcher.dispatchError({ errorCode: 500, errorMessage: `Error deleting all chats for case ${caseId}` });
        throw error;
    }
}

/**
 * Sends a message to a specific chat session.
 * @param {string} caseId The ID of the case.
 * @param {string} sessionId The ID of the chat session.
 * @param {IMessage} message The message to be sent.
 * @param {IMiddlewareParams} middlewareParams Optional parameters to pass to middleware.
 * @returns {Promise<IMessage>} The new message with a timestamp.
 */
export async function sendMessage(
    caseId: string,
    sessionId: string,
    message: IMessage,
    middlewareParams?: IMiddlewareParams
): Promise<IMessage> {
    try {
        // Get the JWT token from the authClient using methods from the documentation
        const authClient = getAuthClient(authClientName);
        // Try ID token first, then fall back to access token
        const jwtToken = authClient.getIdToken() || authClient.getAccessToken();

        if (!jwtToken) {
            throw new AuthenticationError("No authentication token available");
        }

        let url = `${getApiBaseUrl()}${API_PATHS.MESSAGES(caseId, sessionId)}`;

        // Add middleware params to URL if present
        if (middlewareParams && Object.keys(middlewareParams).length > 0) {
            const queryParams = new URLSearchParams();

            Object.entries(middlewareParams).forEach(([key, value]) => {
                if (value !== undefined) {
                    queryParams.append(key, value);
                }
            });

            const queryString = queryParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
        }

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwtToken}`
            },
            body: JSON.stringify(message)
        });

        if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
                throw new AuthenticationError("Authentication failed");
            } else if (response.status === 404) {
                throw new NotFoundError(`Case or session not found`);
            } else {
                throw new ChatServiceError(response.status, `Failed to send message: ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        console.error("Error sending message:", error);

        const errorObj: IError = {
            errorCode: error.code || 500,
            errorMessage: `Error sending message: ${error.message || "Unknown error"}`
        };

        ErrorDispatcher.dispatchError(errorObj); // Emit error event for modal
        throw errorObj;
    }
}
